import { useEffect, useRef, useState } from "react"
import { Form } from "antd"
import { useSelector } from "react-redux"
import { modelSelector } from "store/selectors"
import { WizardContext } from "./WizardContext"
import { buildDefaultValues } from "./mappers/buildDefaultValues"
import "ui/Form/Form.css"

/**
 * Single source of truth for the experience creation wizard.
 *
 * Provides via WizardContext:
 *   form              — single AntD Form instance shared across all steps
 *   mediaFilesRef     — File objects for the media step (not serializable, never in Redux)
 *   stepSaveHandlersRef — steps with non-form local state register async save functions here
 *   trip              — current server state from Redux
 *
 * Form values are initialized once when the trip loads.
 * AntD's preserve=true (default) keeps field values when step components unmount.
 *
 * component="div" (not component={false}) so AntD renders:
 *   <div class="ant-form ant-form-vertical custom-form">
 * This makes all AntD form CSS (.ant-form-vertical, etc.) apply to descendant
 * Form.Items, matching the styling of the old CustomForm-based wizard.
 */
export const WizardProvider = ({ tripId, children }) => {
  const [form] = Form.useForm()
  const [isSaving, setIsSaving] = useState(false)
  const mediaFilesRef = useRef([])
  const stepSaveHandlersRef = useRef({})
  const hasInitialized = useRef(false)

  const trip = useSelector(modelSelector("trip"))

  useEffect(() => {
    if (trip?.id && trip.id === Number(tripId) && !hasInitialized.current) {
      form.setFieldsValue(buildDefaultValues(trip))
      hasInitialized.current = true
    }
  }, [trip, tripId, form])

  // Reset initialization guard when navigating to a different trip
  useEffect(() => {
    hasInitialized.current = false
  }, [tripId])

  return (
    <WizardContext.Provider value={{ form, mediaFilesRef, stepSaveHandlersRef, trip, isSaving, setIsSaving }}>
      <Form
        form={form}
        layout="vertical"
        component="div"
        className="custom-form"
        requiredMark={(label, { required }) => (
          <>
            {label}
            {required && " *"}
          </>
        )}
      >
        {children}
      </Form>
    </WizardContext.Provider>
  )
}
